// Copyright © 2017-2019  SDG Systems, LLC. All rights reserved.
// This file may not be used in any form without the express written permission of SDG Systems, LLC.

<template>
  <v-container fluid grid-list-md>
    <v-row>
      <v-col cols="12" justify-center>
        <v-card>
          <v-card-text>
            <v-container fluid style="padding: 0px;">
              <v-row wrap>
                <v-col cols="12" md="4">
                  <date-time-view
                  v-model="dateStart"
                  clearable
                  label="Start Date"
                  ></date-time-view>
                </v-col>
                <v-col cols="12" md="4">
                  <date-time-view
                  v-model="dateEnd"
                  clearable
                  label="End Date"
                  ></date-time-view>
                </v-col>
                <v-col cols="12" md="4">
                  <v-autocomplete
                  label="Endpoints"
                  item-text="name"
                  return-object
                  v-bind:items="endpoints"
                  v-model="selectedEndpoints"
                  clearable
                  hide-details
                  multiple
                  ></v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-btn dark color="blue darken-1" class="mr-1" @click="resetDate()">Today</v-btn>
                  <v-btn dark color="blue darken-2" class="mr-1" @click="resetWeek()">Last 7 Days</v-btn>
                  <v-btn dark color="blue darken-3" class="mr-1" @click="resetMonth()">Last 30 Days</v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-text style="padding: 0px;">
            <v-data-table
            v-bind:headers="headers"
            :items="events"
            class="elevation-1"
            :loading="loading"
            :options.sync="pagination"
            :update:items-per-page="pagination"
            :server-items-length="totalItems"
            :footer-props="{
              'items-per-page-options':[10,25,100]
            }"
            >
              <template slot="item" slot-scope="props">
                <tr @click="openWorker(props.item)">
                  <td class="text-xs-left">
                    {{ props.item.tag }} - {{ isMatched(props.item.thing)}}
                    <template v-if="props.item.alarmTamper">- <v-icon small color="red">alarm</v-icon></template>
                  </td>
                  <td class="text-xs-left">
                    {{ formatFirstTime(props.item) }}
                  </td>
                  <td class="text-xs-left">
                    {{ formatTime(props.item.time) }}
                  </td>
                  <td class="text-xs-left">
                    {{ formatRepeatCount(props.item) }}
                  </td>
                  <td class="text-xs-left">
                    {{ props.item.direction }}
                  </td>
                  <td class="text-xs-left">
                    {{ getEndpointName(props.item.endpoint) }}
                  </td>
                  <td class="text-xs-left">
                    {{ Math.round(props.item.data.cFloat0 * 10) / 10 }}
                  </td>
                  <td class="text-xs-left">
                    {{ getLocationTagName(props.item.locationTag) }}
                  </td>
                  <td class="text-xs-left">
                    {{ props.item.antenna }}
                  </td>
                  <td class="text-xs-left">
                    {{ getZoneName(props.item.zone) }}
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <div id="eventCanaryElement"></div>
  </v-container>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import DateTimeView from '@/components/DateTimeView'
import ApiRequests from '@/api/ApiRequests'
import { LookupMixin, TimeMixin, EventsMixin } from '@/shared/mixins'

export default {
  components: {
    DateTimeView
  },

  mixins: [LookupMixin, TimeMixin, EventsMixin],

  data () {
    return {
      headers: [
        {
          text: 'Tag',
          align: 'left',
          value: 'tag',
          sortable: false
        },
        {
          text: 'Last Full Event',
          value: 'firstTime',
          sortable: false
        },
        {
          text: 'Last Seen',
          value: 'time',
          sortable: true
        },
        {
          text: 'Repeat Event Count',
          value: 'repeatCount',
          sortable: false
        },
        {
          text: 'Direction',
          align: 'left',
          value: 'direction',
          sortable: false
        },
        {
          text: 'Endpoint',
          align: 'left',
          value: 'endpoint.name',
          sortable: false
        },
        {
          text: 'Data',
          align: 'left',
          value: 'state.locationTag',
          sortable: false
        },
        {
          text: 'Location',
          align: 'left',
          value: 'state.locationTag',
          sortable: false
        },
        {
          text: 'Antenna',
          align: 'left',
          value: 'antenna',
          sortable: false
        },
        {
          text: 'Zone',
          align: 'left',
          value: 'zone',
          sortable: false
        }
      ],
      dateStart: moment().startOf('day').format(),
      dateEnd: moment().endOf('day').format(),
      selectedEndpoints: [],
      endpoints: [],
      events: [],
      loading: true,
      locationTags: [],
      pagination: {itemsPerPage: 25},
      totalItems: 0,
      zones: []
    }
  },

  created () {
    ApiRequests.getEventSources().then(r => {
      this.endpoints = r.data
    })

    ApiRequests.queryLocationTags().then(r => {
      this.locationTags = r.data
    })

    ApiRequests.getZones().then(r => {
      this.zones = r.data
    })
  },

  mounted () {
    this.poll(this.getEvents, 5000)
  },

  computed: {
    ...mapGetters([
      'selectedSite'
    ])
  },

  watch: {
    pagination: function () {
      this.getEvents()
    },

    dateStart: function () {
      this.getEvents()
    },

    dateEnd: function () {
      this.getEvents()
    },

    selectedEndpoints: function () {
      this.getEvents()
    },

    selectedSite: function () {
      this.getEvents()
    }
  },

  methods: {
    isMatched: function (id) {
      if (id === '787878787878787878787878') {
        return '✗'
      }
      return '✔'
    },

    openWorker: function (event) {
      if (event.thing !== '787878787878787878787878') {
        this.$router.push({name: 'thingEdit', params: {id: event.thing}})
      }
    },

    resetDate: function () {
      this.dateStart = moment().startOf('day').format()
      this.dateEnd = moment().endOf('day').format()
    },

    resetWeek: function () {
      this.dateStart = moment().startOf('day').subtract(6, 'days').format()
      this.dateEnd = moment().endOf('day').format()
    },

    resetMonth: function () {
      this.dateStart = moment().startOf('day').subtract(29, 'days').format()
      this.dateEnd = moment().endOf('day').format()
    },

    getEvents: function () {
      this.loading = true
      var params = {}
      params.limit = this.pagination.itemsPerPage
      params.page = (this.pagination.page - 1)

      if (this.dateStart !== '' && this.dateStart) {
        params.after = moment(this.dateStart).format()
      }

      if (this.dateEnd !== '' && this.dateEnd) {
        params.before = moment(this.dateEnd).format()
      }

      if (this.selectedEndpoints.length > 0) {
        var list = this.selectedEndpoints[0]._id
        for (let i = 1; i < this.selectedEndpoints.length; i++) {
          list = list + ','
          list = list + this.selectedEndpoints[i]._id
        }
        params.endpoint = list
      }

      if (this.selectedSite._id) {
        params.site = this.selectedSite._id

        ApiRequests.eventsQuery(params).then(response => {
          this.loading = false
          this.events = response.data
          this.totalItems = parseInt(response.headers['x-total-count'])
        })
      }
    },

    poll: function (fn, interval) {
      if (interval === null) {
        interval = 1000
      }
      var repeatFunc = function () {
        fn()
        // Use this to kill the poll if it's not visible.
        if (document.getElementById('eventCanaryElement')) {
          setTimeout(repeatFunc, interval)
        }
      }
      repeatFunc()
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>



// WEBPACK FOOTER //
// src/components/Events.vue